// Attribution Service for Revenue Tracking
// Connects user behavior to purchase outcomes

interface AttributionData {
  searchTerms: string[];
  filtersApplied: {
    type: string;
    value: string;
    timestamp: number;
  }[];
  utmParameters: {
    utm_source?: string;
    utm_medium?: string;
    utm_campaign?: string;
    utm_term?: string;
    utm_content?: string;
  };
  sessionValue: number;
  landingPage: string;
  referrer: string;
  sessionId: string;
  firstVisit: boolean;
}

class AttributionService {
  private static instance: AttributionService;
  private sessionData: AttributionData;

  private constructor() {
    this.sessionData = this.initializeSession();
  }

  static getInstance(): AttributionService {
    if (!AttributionService.instance) {
      AttributionService.instance = new AttributionService();
    }
    return AttributionService.instance;
  }

  private initializeSession(): AttributionData {
    const urlParams = new URLSearchParams(window.location.search);
    const sessionId = this.getOrCreateSessionId();
    const isFirstVisit = !localStorage.getItem('ss_visited');
    
    if (isFirstVisit) {
      localStorage.setItem('ss_visited', 'true');
    }

    return {
      searchTerms: this.getStoredSearchTerms(),
      filtersApplied: this.getStoredFilters(),
      utmParameters: {
        utm_source: urlParams.get('utm_source') || this.getStoredUTM('utm_source') || undefined,
        utm_medium: urlParams.get('utm_medium') || this.getStoredUTM('utm_medium') || undefined,
        utm_campaign: urlParams.get('utm_campaign') || this.getStoredUTM('utm_campaign') || undefined,
        utm_term: urlParams.get('utm_term') || this.getStoredUTM('utm_term') || undefined,
        utm_content: urlParams.get('utm_content') || this.getStoredUTM('utm_content') || undefined,
      },
      sessionValue: 0,
      landingPage: window.location.pathname + window.location.search,
      referrer: document.referrer,
      sessionId,
      firstVisit: isFirstVisit
    };
  }

  private getOrCreateSessionId(): string {
    const existing = sessionStorage.getItem('ss_session_id');
    if (existing) return existing;
    
    const sessionId = `ss_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    sessionStorage.setItem('ss_session_id', sessionId);
    return sessionId;
  }

  private getStoredSearchTerms(): string[] {
    try {
      return JSON.parse(sessionStorage.getItem('ss_search_terms') || '[]');
    } catch {
      return [];
    }
  }

  private getStoredFilters(): AttributionData['filtersApplied'] {
    try {
      return JSON.parse(sessionStorage.getItem('ss_filters_applied') || '[]');
    } catch {
      return [];
    }
  }

  private getStoredUTM(param: string): string | null {
    return sessionStorage.getItem(`ss_${param}`);
  }

  // Store UTM parameters for the session
  storeUTMParameters() {
    Object.entries(this.sessionData.utmParameters).forEach(([key, value]) => {
      if (value) {
        sessionStorage.setItem(`ss_${key}`, value);
      }
    });
  }

  // Track search terms for attribution
  trackSearchTerm(searchTerm: string) {
    if (!searchTerm.trim()) return;
    
    this.sessionData.searchTerms.push(searchTerm);
    
    // Keep only last 10 search terms
    if (this.sessionData.searchTerms.length > 10) {
      this.sessionData.searchTerms = this.sessionData.searchTerms.slice(-10);
    }
    
    sessionStorage.setItem('ss_search_terms', JSON.stringify(this.sessionData.searchTerms));
  }

  // Track filter applications for attribution
  trackFilterApplication(filterType: string, filterValue: string) {
    const filterData = {
      type: filterType,
      value: filterValue,
      timestamp: Date.now()
    };
    
    this.sessionData.filtersApplied.push(filterData);
    
    // Keep only filters from last 30 minutes
    const thirtyMinutesAgo = Date.now() - (30 * 60 * 1000);
    this.sessionData.filtersApplied = this.sessionData.filtersApplied.filter(
      filter => filter.timestamp > thirtyMinutesAgo
    );
    
    sessionStorage.setItem('ss_filters_applied', JSON.stringify(this.sessionData.filtersApplied));
  }

  // Update session value based on cart additions
  updateSessionValue(additionalValue: number) {
    this.sessionData.sessionValue += additionalValue;
    sessionStorage.setItem('ss_session_value', this.sessionData.sessionValue.toString());
  }

  // Get attribution data for ecommerce events
  getAttributionData(): AttributionData & {
    search_attribution: string[];
    filter_attribution: string[];
    utm_attribution: object;
    session_insights: {
      value: number;
      search_count: number;
      filter_count: number;
      session_duration: number;
    };
  } {
    const sessionStart = parseInt(sessionStorage.getItem('ss_session_start') || Date.now().toString());
    const sessionDuration = Math.round((Date.now() - sessionStart) / 1000);

    return {
      ...this.sessionData,
      search_attribution: this.sessionData.searchTerms,
      filter_attribution: this.sessionData.filtersApplied.map(f => `${f.type}:${f.value}`),
      utm_attribution: this.sessionData.utmParameters,
      session_insights: {
        value: this.sessionData.sessionValue,
        search_count: this.sessionData.searchTerms.length,
        filter_count: this.sessionData.filtersApplied.length,
        session_duration: sessionDuration
      }
    };
  }

  // Clear session data (for new sessions)
  clearSession() {
    sessionStorage.removeItem('ss_search_terms');
    sessionStorage.removeItem('ss_filters_applied');
    sessionStorage.removeItem('ss_session_value');
    this.sessionData = this.initializeSession();
  }

  // Track session start
  initializeSessionTracking() {
    if (!sessionStorage.getItem('ss_session_start')) {
      sessionStorage.setItem('ss_session_start', Date.now().toString());
    }
    this.storeUTMParameters();
  }
}

export default AttributionService;